import styles from './Calendar.module.css';

/**
 * Calendar component for date selection
 *
 * @param {Object} props - Component props
 * @param {Date} props.currentMonth - Current month being displayed
 * @param {Function} props.onMonthChange - Function to change the current month
 * @param {Date} props.selectedDate - Currently selected date
 * @param {Function} props.onDateSelect - Function called when a date is selected
 * @param {Function} props.onCancel - Function called when cancel is clicked
 * @param {Function} props.onSave - Function called when save is clicked
 * @param {boolean} props.openAbove - Whether calendar opens above the input
 * @param {Date} props.tempSelectedDate - Temporarily selected date in calendar
 */
function Calendar({
  currentMonth,
  onMonthChange,
  selectedDate,
  onDateSelect,
  onCancel,
  onSave,
  openAbove = false,
  tempSelectedDate,
}) {
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const dayNames = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  const goToPreviousMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() - 1);
    onMonthChange(newMonth);
  };

  const goToNextMonth = () => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + 1);

    // Don't allow navigation to future months
    const today = new Date();
    if (
      newMonth.getFullYear() > today.getFullYear() ||
      (newMonth.getFullYear() === today.getFullYear() &&
        newMonth.getMonth() > today.getMonth())
    ) {
      return; // Don't navigate to future months
    }

    onMonthChange(newMonth);
  };

  const isNextMonthDisabled = () => {
    const nextMonth = new Date(currentMonth);
    nextMonth.setMonth(currentMonth.getMonth() + 1);
    const today = new Date();
    return (
      nextMonth.getFullYear() > today.getFullYear() ||
      (nextMonth.getFullYear() === today.getFullYear() &&
        nextMonth.getMonth() > today.getMonth())
    );
  };

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Previous month's trailing days
    const prevMonth = new Date(year, month - 1, 0);
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false,
      });
    }

    // Current month's days
    for (let day = 1; day <= daysInMonth; day++) {
      days.push({
        date: new Date(year, month, day),
        isCurrentMonth: true,
      });
    }

    // Next month's leading days
    const remainingDays = 42 - days.length; // 6 rows × 7 days
    for (let day = 1; day <= remainingDays; day++) {
      days.push({
        date: new Date(year, month + 1, day),
        isCurrentMonth: false,
      });
    }

    return days;
  };

  const isDateSelected = date => {
    // Check against tempSelectedDate first (for current calendar selection), then selectedDate (for saved selection)
    const dateToCheck = tempSelectedDate || selectedDate;
    if (!dateToCheck) return false;
    return (
      date.getDate() === dateToCheck.getDate() &&
      date.getMonth() === dateToCheck.getMonth() &&
      date.getFullYear() === dateToCheck.getFullYear()
    );
  };

  const isFutureDate = date => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate > today;
  };

  const days = getDaysInMonth();

  return (
    <div className={`${styles.calendar} `}>
      <div className={styles.header}>
        <h3 className={styles.monthYear}>
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>

        <div className={styles.navButtons}>
          <button
            type="button"
            className={styles.navButton}
            onClick={goToPreviousMonth}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M15 18L9 12L15 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <button
            type="button"
            className={`${styles.navButton} ${isNextMonthDisabled() ? styles.disabled : ''}`}
            onClick={goToNextMonth}
            disabled={isNextMonthDisabled()}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M9 18L15 12L9 6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
      </div>

      <div className={styles.grid}>
        <div className={styles.dayHeaders}>
          {dayNames.map((day, index) => (
            <div key={index} className={styles.dayHeader}>
              {day}
            </div>
          ))}
        </div>

        <div className={styles.daysGrid}>
          {days.map((dayObj, index) => {
            const isDisabled = isFutureDate(dayObj.date);
            return (
              <button
                key={index}
                type="button"
                className={`${styles.dayCell} ${
                  !dayObj.isCurrentMonth ? styles.otherMonth : ''
                } ${isDateSelected(dayObj.date) ? styles.selected : ''} ${
                  isDisabled ? styles.disabled : ''
                }`}
                onClick={() => !isDisabled && onDateSelect(dayObj.date)}
                disabled={isDisabled}
              >
                {dayObj.date.getDate()}
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.footer}>
        <button
          type="button"
          className={styles.actionButton}
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className={styles.actionButton}
          onClick={onSave}
          disabled={!tempSelectedDate}
        >
          Save
        </button>
      </div>
    </div>
  );
}

export default Calendar;
